import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { 
  PS_DIMENSIONS, 
  teamComparisonData, 
  getPSICategory, 
  getAIRecommendations 
} from '../data/psychologicalSafetyLogic';

export const PsychologicalSafetyIndex = () => {
  const [selectedTeamId, setSelectedTeamId] = useState(teamComparisonData[0].id);
  const selectedTeam = useMemo(() => 
    teamComparisonData.find(t => t.id === selectedTeamId), 
    [selectedTeamId]
  );
  
  const category = getPSICategory(selectedTeam.psi);
  const aiResults = getAIRecommendations(selectedTeam);

  const radarOption = {
    color: ['#6366f1'],
    radar: {
      indicator: PS_DIMENSIONS.map(d => ({ name: d.name, max: 100 })),
      splitNumber: 4,
      axisName: { color: '#64748b', fontWeight: 'bold', fontSize: 11 },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
      splitArea: { show: false }
    },
    series: [{
      type: 'radar',
      areaStyle: { color: 'rgba(99, 102, 241, 0.1)' },
      data: [{ value: PS_DIMENSIONS.map(d => selectedTeam.scores[d.id]), name: selectedTeam.name }]
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Live Analyse</span>
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Psykologisk Trygghet</h1>
          <p className="text-slate-500 font-medium">Måling av teamets fundament for innovasjon og læring</p>
        </div>
        <select 
          className="px-6 py-3 bg-white border-2 border-slate-100 rounded-2xl text-sm font-bold shadow-sm outline-none focus:border-indigo-500 transition-all"
          value={selectedTeamId}
          onChange={(e) => setSelectedTeamId(e.target.value)}
        >
          {teamComparisonData.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Score Overview */}
        <Card className="lg:col-span-1 border-none shadow-2xl shadow-slate-200/50 bg-gradient-to-br from-indigo-600 to-violet-700 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <SafeIcon name="Shield" size={120} />
          </div>
          <CardContent className="p-10 space-y-10 relative z-10">
            <div>
              <p className="text-indigo-100 text-xs font-black uppercase tracking-[0.2em] mb-2">Psychological Safety Index</p>
              <div className="flex items-baseline gap-2">
                <span className="text-7xl font-black tracking-tighter">{selectedTeam.psi}</span>
                <span className="text-2xl font-bold text-indigo-300">/100</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs font-bold uppercase">
                <SafeIcon name="Star" className="text-amber-300" /> {category.label}
              </div>
              <p className="text-indigo-100/80 text-sm leading-relaxed font-medium">
                Dette teamet har et klima hvor {selectedTeam.psi > 70 ? 'medlemmer føler seg trygge på å ta risiko uten frykt for sanksjoner.' : 'det er behov for målrettede tiltak for å øke ytringsviljen.'}
              </p>
            </div>

            <Button className="w-full bg-white text-indigo-600 hover:bg-indigo-50 border-none py-6 font-black uppercase tracking-wider text-xs shadow-xl">
              Generer Leder-Briefing
            </Button>
          </CardContent>
        </Card>

        {/* Detailed Breakdown */}
        <Card className="lg:col-span-2 border-none shadow-xl">
          <CardHeader 
            title="Dybdeanalyse" 
            subtitle="Fordeling på Edmondsons 6 dimensjoner"
            icon={<SafeIcon name="Target" className="text-indigo-600" />}
          />
          <CardContent className="h-[400px]">
            <ReactECharts option={radarOption} style={{ height: '100%' }} />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recommendations */}
        <Card className="border-none shadow-xl bg-slate-50">
          <CardHeader title="Forbedringstiltak" subtitle="AI-genererte råd for teamet" icon={<SafeIcon name="Zap" className="text-amber-500" />} />
          <CardContent className="p-8 space-y-4">
            {aiResults.tips.map((tip, i) => (
              <div key={i} className="flex gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm group hover:border-indigo-500 transition-all">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                  <span className="text-sm font-black">{i + 1}</span>
                </div>
                <p className="text-sm text-slate-700 font-bold leading-snug self-center">{tip}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Workshop Suggestions */}
        <Card className="border-none shadow-xl border-l-4 border-l-emerald-500">
          <CardHeader title="Foreslåtte Workshops" subtitle="Ferdigstilte opplegg for teamet" icon={<SafeIcon name="Users" className="text-emerald-500" />} />
          <CardContent className="p-8 space-y-4">
            {aiResults.workshops.map((ws, i) => (
              <div key={i} className="flex items-center justify-between p-5 bg-emerald-50/50 rounded-2xl border border-emerald-100 group">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-500 text-white rounded-xl shadow-lg shadow-emerald-100">
                    <SafeIcon name="Calendar" size={20} />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight">{ws}</h4>
                    <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest mt-1">Estimert: 2 timer</p>
                  </div>
                </div>
                <Button variant="secondary" size="sm" className="bg-white border-emerald-200 text-emerald-700 font-bold text-[10px] uppercase">Book nå</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PsychologicalSafetyIndex;